import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Call } from '@gem/call/CallStore/Call';
import { Opponent } from '@gem/call/CallStore/Opponent/Opponent';
import { useCallContext } from '@gem/call/CallView/CallView.context';
import { useOpponentsListSelector } from '@gem/call/CallView/hooks/useOpponentsListSelector/useOpponentsListSelector';
import { CallPermissionsPopup } from '@gem/call/popups/Permissions/CallPermissionsPopup/CallPermissionsPopup';
import { StartRecordingPopup } from '@gem/call/popups/StartRecordingPopup/StartRecordingPopup';
import { copyUnionCallLink } from '@gem/call/tasks/copyUnionCallLink';
import {
  CopyIcon,
  IcAddUserIcon,
  IcBackgroundEffectIcon,
  IcLockSmallIcon,
  IcStartRecordingIcon,
  IcStopRecordingIcon,
  IcVideoGridIcon,
  IcVideoListIcon,
} from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { popupsTasks, PopupStore } from '@gem/popups';
import { IPopoverMenuItem } from '@gem/ui-kit';

interface IShowMoreButtonPresenter {
  opponentsList: readonly Opponent[];
  isPopoverOpened: boolean;
  isVisibleStopRecordingDialog: boolean;
  ongoingCall: Call;
  title: string;
  menu: IPopoverMenuItem[];
  childPopup: PopupStore | null;
  togglePopover(): void;
  closePopover(): void;
  copyInviteLinkToClipboard(): void;
  onStopRecording(): Promise<void>;
  showMembersList(): void;
  showAddMembersPopup(): void;
  showBackgroundPanel(): void;
  openStartRecordingPopup(): void;
  showPermissionsPopup(): void;
  changeLayout(): void;
  setOpponentsList(opponentsList: readonly Opponent[]): void;
}

export const useShowMoreButtonPresenter = (): IShowMoreButtonPresenter => {
  const { t } = useTranslation();
  const context = useCallContext();
  const opponentsList = useOpponentsListSelector();

  const presenter = useLocalObservable<IShowMoreButtonPresenter>(() => ({
    opponentsList: opponentsList,
    isPopoverOpened: false,
    isVisibleStopRecordingDialog: false,
    childPopup: null,

    get ongoingCall(): Call {
      return context.call;
    },

    get title(): string {
      return t('call_recording_rec_hint');
    },

    get menu(): IPopoverMenuItem[] {
      const list = [];
      const call = presenter.ongoingCall;
      const hasAddMembersButton =
        call.uiFeatures.showControlInviteMembers && call.isAnswered && !call.isUnauthorizedCall;

      if (hasAddMembersButton) {
        list.push({
          key: 'add-people',
          icon: IcAddUserIcon,
          title: t('common_add_people'),
          onClick: presenter.showAddMembersPopup,
        });
      }

      if (call.uiFeatures.showCopyLinkInMoreMenu) {
        list.push({
          key: 'copy-invite-link',
          icon: CopyIcon,
          title: t('common_copy_invite_link'),
          onClick: () =>
            copyUnionCallLink({
              channelId: presenter.ongoingCall.channelId || undefined,
              inviteLink: presenter.ongoingCall.inviteLink,
              copyCallback: presenter.ongoingCall.setInviteLink,
            }),
        });
      }

      if (call.uiFeatures.showControlBackground && context.callController.canUseBlur) {
        list.push({
          key: 'apply-background',
          icon: IcBackgroundEffectIcon,
          title: t('call_apply_background'),
          onClick: presenter.showBackgroundPanel,
        });
      }

      // hide layoutButton for compact view in audio groupCall
      const hasLayoutButton = presenter.opponentsList.length > 1;

      if (hasLayoutButton) {
        const isGridLayout = presenter.ongoingCall.viewState.isGridLayout;

        list.push({
          key: 'change-layout',
          icon: isGridLayout ? IcVideoListIcon : IcVideoGridIcon,
          title: isGridLayout ? t('calls_speaker_view') : t('calls_grid_view'),
          onClick: presenter.changeLayout,
        });
      }

      if (call.canStopRecording) {
        list.push({
          key: 'stop-recording',
          icon: IcStopRecordingIcon,
          title: t('calls_recording_stop_button'),
          onClick: presenter.onStopRecording,
        });
      }

      if (
        call.uiFeatures.showControlRecording &&
        call.canStartRecording &&
        !call.isUnauthorizedCall
      ) {
        list.push({
          key: 'start-recording',
          icon: IcStartRecordingIcon,
          title: t('calls_recording_start_button'),
          onClick: presenter.openStartRecordingPopup,
        });
      }

      if (call.uiFeatures.permissionsAndAccessControlEnabled) {
        list.push({
          key: 'permissions',
          icon: IcLockSmallIcon,
          title: t('calls_kebab_access_permissens_button'),
          onClick: presenter.showPermissionsPopup,
        });
      }

      return list;
    },

    togglePopover(): void {
      this.isPopoverOpened = !this.isPopoverOpened;
    },

    closePopover(): void {
      this.isPopoverOpened = false;
    },

    copyInviteLinkToClipboard(): void {
      copyUnionCallLink({
        channelId: presenter.ongoingCall.channelId || undefined,
        inviteLink: presenter.ongoingCall.inviteLink,
        copyCallback: presenter.ongoingCall.setInviteLink,
      });
    },

    async onStopRecording(): Promise<void> {
      presenter.closePopover();

      const canStop = await popupsTasks.showConfirm({
        text: t('calls_recording_stop_alert'),
        primaryText: t('common_stop'),
      });

      if (canStop) {
        await context.callController.stopRecording();
      }
    },

    showMembersList(): void {
      presenter.closePopover();

      if (presenter.ongoingCall.viewState.backgroundPanelShown) {
        presenter.ongoingCall.viewState.hideBackgroundPanel();
      }
      presenter.ongoingCall.viewState.showMembers();
    },

    showAddMembersPopup(): void {
      presenter.closePopover();
      if (context.callController.handlers.onAddCallMembers) {
        context.callController.handlers.onAddCallMembers(context.call);
      }
    },

    showBackgroundPanel(): void {
      if (presenter.ongoingCall.meStore.isVideoMuted) {
        popupsTasks.showAlert(t('call_background_camera_is_off_message'));

        return;
      }

      if (presenter.ongoingCall.viewState.isMembersShown) {
        presenter.ongoingCall.viewState.hideMembers();
      }

      presenter.closePopover();
      presenter.ongoingCall.viewState.toggleBackgroundPanel();
    },

    openStartRecordingPopup(): void {
      presenter.closePopover();

      if (!presenter.ongoingCall.isMeInitiatorOwnerOrAdmin) {
        popupsTasks.showToast(t('moderation_content_limit_media'));
        return;
      }

      presenter.childPopup = popupsTasks.openPopup(StartRecordingPopup, {
        params: {
          call: presenter.ongoingCall,
          callController: context.callController,
        },
      });
    },

    showPermissionsPopup(): void {
      presenter.closePopover();
      popupsTasks.openPopup(CallPermissionsPopup, {
        params: {
          call: presenter.ongoingCall,
          callController: context.callController,
        },
      });
    },

    changeLayout(): void {
      presenter.closePopover();
      context.callController.toggleGridLayout(null);
    },

    setOpponentsList(opponentsList: readonly Opponent[]): void {
      presenter.opponentsList = opponentsList;
    },
  }));

  useEffect(() => {
    return () => {
      presenter.childPopup?.close();
    };
  }, []);

  useEffect(() => {
    presenter.setOpponentsList(opponentsList);
  }, [opponentsList]);

  return presenter;
};
